import { combineReducers, Reducer } from "redux";
import produce from "immer";

import { DATA_SOURCE_INIT_STATE } from "../initState";
import {
    IDataSourceConfig,
    IInitDataSourceAction,
    ISetAttributesAction,
} from "../types";

const objectName: Reducer<IDataSourceConfig["objectName"]> = (
    state = DATA_SOURCE_INIT_STATE.config.objectName,
    action,
) => {
    if (action instanceof IInitDataSourceAction) {
        return action.payload.config.objectName;
    }
    return state;
};

const attributes: Reducer<IDataSourceConfig["attributes"]> = (
    state = DATA_SOURCE_INIT_STATE.config.attributes,
    action,
) => {
    if (action instanceof IInitDataSourceAction || action instanceof ISetAttributesAction) {
        return produce(state, draft => {
            const attributes = (action instanceof IInitDataSourceAction) ? action.payload.config.attributes : action.payload.attributes;

            attributes.forEach(attribute => {
                const attributeName = attribute.attributeName.toLocaleLowerCase();
                draft[attributeName] = attribute;
                draft[attributeName].attributeName = attributeName;
                draft[attributeName].backgroundAttrName = attribute.backgroundAttrName?.toLocaleLowerCase() ?? null;
            },
            );
        });
    }
    return state;
};

const attributeNames: Reducer<IDataSourceConfig["attributeNames"]> = (
    state = DATA_SOURCE_INIT_STATE.config.attributeNames,
    action,
) => {
    if (action instanceof IInitDataSourceAction) {
        return action.payload.config.attributes.map(attribute => attribute.attributeName.toLocaleLowerCase());
    } else if (action instanceof ISetAttributesAction) {
        return action.payload.attributes.map(attribute => attribute.attributeName.toLocaleLowerCase());
    }
    return state;
};

const fullLengthAttributes: Reducer<IDataSourceConfig["fullLengthAttributes"]> = (
    state = DATA_SOURCE_INIT_STATE.config.fullLengthAttributes,
    action,
) => {
    if (action instanceof IInitDataSourceAction) {
        return action.payload.config.attributes.filter(attribute => attribute.trimValue === false).map(attribute => attribute.attributeName.toLocaleLowerCase());
    } else if (action instanceof ISetAttributesAction) {
        return action.payload.attributes.filter(attribute => attribute.trimValue === false).map(attribute => attribute.attributeName.toLocaleLowerCase());
    }
    return state;
};

const recordIdKey: Reducer<IDataSourceConfig["recordIdKey"]> = (
    state = DATA_SOURCE_INIT_STATE.config.recordIdKey,
    action,
) => {
    if (action instanceof IInitDataSourceAction) {
        return action.payload.config.recordIdKey;
    }
    return state;
};

const formContext: Reducer<IDataSourceConfig["formContext"]> = (
    state = DATA_SOURCE_INIT_STATE.config.formContext,
    action,
) => {
    if (action instanceof IInitDataSourceAction) {
        if (action.payload.config) {
            return action.payload.config.formContext;
        }
    }
    return state;
};

const hasWorkflow: Reducer<IDataSourceConfig["hasWorkflow"]> = (
    state = DATA_SOURCE_INIT_STATE.config.hasWorkflow,
    action,
) => {
    if (action instanceof IInitDataSourceAction) {
        return action.payload.config.hasWorkflow;
    }
    return state;
};

const isPersistent: Reducer<IDataSourceConfig["isPersistent"]> = (
    state = DATA_SOURCE_INIT_STATE.config.isPersistent,
    action,
) => {
    if (action instanceof IInitDataSourceAction) {
        if (action.payload.config) {
            return action.payload.config.isPersistent;
        }
    }
    return state;
};

const customDataSourceId: Reducer<IDataSourceConfig["customDataSourceId"] | undefined> = (
    state = DATA_SOURCE_INIT_STATE.config.customDataSourceId,
    action,
) => {
    if (action instanceof IInitDataSourceAction) {
        if (action.payload.config?.customDataSourceId) {
            return action.payload.config?.customDataSourceId;
        }
    }
    return state;
};

const customMethodsData: Reducer<IDataSourceConfig["customMethodsData"] | undefined> = (
    state = DATA_SOURCE_INIT_STATE.config.customMethodsData,
    action,
) => {
    if (action instanceof IInitDataSourceAction) {
        if (action.payload.config?.customMethodsData) {
            return action.payload.config.customMethodsData;
        }
    }
    return state;
};

export const configReducer: Reducer<IDataSourceConfig> = combineReducers<IDataSourceConfig>({
    objectName,
    attributes,
    attributeNames,
    fullLengthAttributes,
    recordIdKey,
    formContext,
    hasWorkflow,
    isPersistent,
    customDataSourceId,
    customMethodsData,
});
